// @flow
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Form } from 'semantic-grab-ui/src';
import { Select, Row, Col, Checkbox } from 'grab-ui';
import formComponent from 'components/decorators/form_component';
import type { TridentCampaignDetails } from 'types/trident-campaigns';
import FormInputWithError from 'components/form/FormInputWithError';
import './TreatmentActionForm.scss';

import _ from 'lodash';
import { Link } from 'react-router';
import {
  GRABPOINT,
  ACTION_AWARD_POINT_FORM_PRICE_PER_POINT_DEFAULT_VALUE,
  ACTION_AWARD_POINT_FORM_CURRENCY_DEFAULT_VALUE,
} from './constants/campaign';
import { getDynamicInputTypes, getAwardPointAction } from 'actions/newTridentExtra';
import { defaultPurposeList, defaultBudgetResourceList } from 'data/trident-campaigns';

type Props = {
  model: TridentCampaignDetails,
  currentNode: string,
  subActionIndex: number,
  onModelChange: (_?: Object) => void,
  handleInputChange: Function,
  handleControlBlur: Function,
  getDynamicInputTypes: Function,
  getAwardPointAction: Function,
  dynamicInputTypes: Array,
  apcPurpose: Array,
  apcBudgetSource: Array,
  actionType: String,
  eventType: String,
  enableDynamicPoint: Boolean,
  readonly: Boolean,
};

const pointCurrencyList = [
  { key: GRABPOINT, value: GRABPOINT, text: GRABPOINT },
  { key: 'OVO-POINT', value: 'OVO-POINT', text: 'OVO-POINT' },
];

const colStyle = { padding: '0px 5px 5px 0px' };

@formComponent()
class ActionAwardPointsForm extends Component {
  props: Props;

  purposeList = defaultPurposeList;
  budgetSourceList = defaultBudgetResourceList;

  state = {
    newPointFormat: {
      point: '',
      variableName: '',
    },
  }

  componentDidMount() {
    const { model, currentNode, subActionIndex, onModelChange } = this.props;
    const updatedModel = _.cloneDeep(model);
    let isRerenderRequired = false;

    const defaultValueList = [{
      fieldName: 'financeData.pricePerPoint', value: ACTION_AWARD_POINT_FORM_PRICE_PER_POINT_DEFAULT_VALUE,
    }, {
      fieldName: 'financeData.fiatCurrency', value: ACTION_AWARD_POINT_FORM_CURRENCY_DEFAULT_VALUE,
    }, {
      fieldName: 'pointCurrency', value: GRABPOINT,
    }];

    defaultValueList.forEach(({ fieldName, value }) => {
      const metaFieldPath = `nodes[${currentNode}].data[${subActionIndex}].meta.${fieldName}`;
      if (_.get(updatedModel, metaFieldPath) == null) {
        _.set(updatedModel, metaFieldPath, value);
        isRerenderRequired = true;
      }
    });

    // trigger rerender
    if (isRerenderRequired) {
      onModelChange(updatedModel);
      this.forceUpdate();
    }

    // set default value for dynamicPoint
    const meta = model.nodes[currentNode].data[subActionIndex].meta;
    if (!meta.dynamicPoint) {
      meta.dynamicPoint = `${meta.point || ''}`;
    }

    this.processDynamicValue(meta.dynamicPoint);

    // Load dynamic input types
    this.props.getDynamicInputTypes();

    // Load award point
    this.props.getAwardPointAction();
  }

  // analyze the dynamic value to proper components
  // 5*[transactionValue] => 5 text input * transactionValue into select
  processDynamicValue = (value = '') => {
    if (value.includes('*')) {
      const splitters = value.split('*').map(x => x.trim());
      this.setState({
        newPointFormat: {
          point: splitters[0],
          variableName: splitters[1],
        },
      });
    } else {
      this.setState({
        newPointFormat: {
          point: value.trim(),
          variableName: '',
        },
      });
    }
  }

  // handle custom input change into formComponent modelChange
  newPointFormatChange = (prop, value, inputKey) => {
    this.setState(
      {
        newPointFormat: {
          ...this.state.newPointFormat,
          [prop]: value,
        },
      },
      () => {
        const { newPointFormat } = this.state;
        const { currentNode, subActionIndex } = this.props;
        const isEnableDynamicPoint = !_.isEmpty(newPointFormat.variableName);
        const pointCapKey = `nodes.${currentNode}.data.${subActionIndex}.meta.pointCap`;
        const simulation = {
          name: inputKey,
          value: isEnableDynamicPoint
            ? `${newPointFormat.point}*${newPointFormat.variableName}`
            : newPointFormat.point,
        };
        this.props.handleInputChange({}, simulation);

        // Reset capPoint value
        if (!isEnableDynamicPoint) {
          setTimeout(() => {
            this.props.handleInputChange(
              {},
              {
                name: pointCapKey,
                value: 0,
              },
            );
          }, 10);
        }
      },
    );
  }

  getCappingComponent = (dynamicVariables = []) => {
    const { currentNode, subActionIndex, enableDynamicPoint, model } = this.props;
    const { newPointFormat } = this.state;
    const isEnableDynamicPoint = enableDynamicPoint || newPointFormat.variableName;
    const key = `nodes.${currentNode}.data.${subActionIndex}.meta.pointCap`;
    const pointCapValue = _.get(model, key, 0);
    const isUnlimited = pointCapValue !== '' && Number(pointCapValue) === 0;

    const handleUnlimitedCheckbox = (e) => {
      const checked = e.target.checked;
      const simulation = {
        name: key,
        value: checked ? 0 : 1,
      };
      this.props.handleInputChange({}, simulation);
    };

    const interceptInputChange = (e, data) => {
      this.props.handleInputChange(
        {},
        {
          name: key,
          value: Number(data.value) || 0,
        },
      );
    };

    // Only enabling when in the dynamic point scenario
    return dynamicVariables.length > 0 && isEnableDynamicPoint && !!newPointFormat.variableName ? (
      <Row>
        <Col span={7}>
          <FormInputWithError
            name={key}
            {...this.props}
          >
            <Form.Input
              type="number"
              min={0}
              label="Point capping"
              value={pointCapValue}
              onChange={interceptInputChange}
              disabled={isUnlimited}
            />
          </FormInputWithError>
        </Col>
        <Col span={6} style={{ margin: '40px 0px 0px 20px' }}>
          <Checkbox
            checked={isUnlimited}
            onChange={e => handleUnlimitedCheckbox(e)}
          >
            Unlimited
          </Checkbox>
        </Col>
      </Row>
    ) : null;
  };

  getPointComponent = (dynamicVariables = []) => {
    const { currentNode, subActionIndex, enableDynamicPoint } = this.props;
    const { newPointFormat } = this.state;
    const isEnableDynamicPoint = enableDynamicPoint || newPointFormat.variableName;
    return dynamicVariables.length > 0 && isEnableDynamicPoint ? (
      <Row>
        <Col span={7}>
          <FormInputWithError
            {...this.props}
          >
            <Form.Input
              required
              label="Points"
              value={newPointFormat.point}
              onChange={(e, data) =>
                this.newPointFormatChange('point', data.value, `nodes.${currentNode}.data.${subActionIndex}.meta.dynamicPoint`)
              }
            />
          </FormInputWithError>
        </Col>
        <div style={{ marginTop: 25 }}>
          <Col span={2}>
            <span
              style={{
                marginLeft: 25,
                top: 10,
                position: 'absolute',
              }}
            >
                x
            </span>
          </Col>
          <Col span={15}>
            <Select
              disabled={this.props.readonly}
              onSelect={(value) =>
                this.newPointFormatChange('variableName', value || '', `nodes.${currentNode}.data.${subActionIndex}.meta.dynamicPoint`)
              }
              value={newPointFormat.variableName}
            >
              {dynamicVariables.map(x => (
                <Select.Option value={x.variableName}>
                  {x.noun}
                </Select.Option>
              ))}
            </Select>
            <span style={{ marginTop: 5 }}>Learn more on <a rel="noopener noreferrer" href="https://wiki.grab.com/display/MKT/How+Do+I+Use+Transaction+Value+Multiplier" target="_blank">Transaction Value Multiplier</a></span>
          </Col>
        </div>
      </Row>
    ) : (
      <FormInputWithError
        name={`nodes.${currentNode}.data.${subActionIndex}.meta.dynamicPoint`}
        {...this.props}
      >
        <Form.Input
          required
          label="Points"
          value={
            this.props.model.nodes[currentNode].data[subActionIndex].meta.dynamicPoint
          }
          onChange={this.props.handleInputChange}
          onBlur={this.props.handleControlBlur}
        />
      </FormInputWithError>
      // </Grid.Column>
    );
  }

  render() {
    const { model, currentNode, subActionIndex, dynamicInputTypes, apcPurpose, apcBudgetSource } = this.props;

    const pointCurrency = _.get(model, `nodes[${currentNode}].data[${subActionIndex}].meta.pointCurrency`, GRABPOINT);
    const isGrabPointCurrency = pointCurrency === GRABPOINT;

    let dynamicVariables = [];
    if (dynamicInputTypes && dynamicInputTypes.length) {
      dynamicVariables = dynamicInputTypes.filter(
        x =>
          x.actions.some(y => y === this.props.actionType) &&
          x.events.some(y => y === this.props.eventType),
      );

      // if current case has dynamic variables from UCM, add default item
      if (dynamicVariables.length > 0) {
        dynamicVariables.unshift({
          noun: 'Fixed',
          variableName: '',
        });
      }
    }

    // purpose
    if (apcPurpose.length) {
      this.purposeList = apcPurpose.map(x => ({ key: x.value, value: x.value, text: x.text }));
    }

    // budget source
    if (apcBudgetSource.length) {
      this.budgetSourceList = apcBudgetSource.map(x => ({ key: x.value, value: x.value, text: x.text }));
    }

    return (
      <div className="treatment-form">
        <div>
          <br />
          <Form>
            <Row>
              <Col span={10}>
                <p>
                  <FormInputWithError
                    name={`nodes.${currentNode}.data.${subActionIndex}.meta.pointCurrency`}
                    {...this.props}
                  >
                    <Form.Select
                      label="Point Type"
                      options={pointCurrencyList}
                      value={pointCurrency}
                      onChange={this.props.handleInputChange}
                      onBlur={this.props.handleControlBlur}
                    />
                  </FormInputWithError>
                </p>
                <p>{this.getPointComponent(dynamicVariables)}</p>
                <p>{this.getCappingComponent(dynamicVariables)}</p>
                <p>
                  <FormInputWithError
                    name={`nodes.${currentNode}.data.${subActionIndex}.meta.description`}
                    {...this.props}
                  >
                    <Form.TextArea
                      required
                      label="Description for notification"
                      value={
                        model.nodes[currentNode].data[subActionIndex].meta
                          .description
                      }
                      onChange={this.props.handleInputChange}
                      onBlur={this.props.handleControlBlur}
                    />
                  </FormInputWithError>
                </p>
              </Col>
              <Col span={12} />
            </Row>
            <Row style={{ marginTop: 25 }}>
              <Col span={10}>
                <Row stype={{ marginTop: 10 }}>
                  <Col span={12} style={colStyle}>
                    <FormInputWithError
                      name={`nodes.${currentNode}.data.${subActionIndex}.meta.purpose`}
                      {...this.props}
                    >
                      <Form.Select
                        required
                        label="Purpose"
                        options={this.purposeList}
                        value={_.get(model, `nodes.${currentNode}.data.${subActionIndex}.meta.purpose`)}
                        onChange={this.props.handleInputChange}
                        onBlur={this.props.handleControlBlur}
                      />
                    </FormInputWithError>
                  </Col>
                  <Col span={12} style={colStyle}>
                    <FormInputWithError
                      name={`nodes.${currentNode}.data.${subActionIndex}.meta.budgetSource`}
                      {...this.props}
                    >
                      <Form.Select
                        required
                        label="Budget Source"
                        options={this.budgetSourceList}
                        value={_.get(model, `nodes.${currentNode}.data.${subActionIndex}.meta.budgetSource`)}
                        onChange={this.props.handleInputChange}
                        onBlur={this.props.handleControlBlur}
                      />
                    </FormInputWithError>
                  </Col>
                </Row>
                <Row stype={{ marginTop: 10 }}>
                  <Col span={12} style={colStyle}>
                    <FormInputWithError
                      name={`nodes.${currentNode}.data.${subActionIndex}.meta.gpFunding`}
                      {...this.props}
                    >
                      <Form.Input
                        required
                        label="Percentage of GP funding"
                        type="number"
                        min={0}
                        max={100}
                        value={_.get(model, `nodes.${currentNode}.data.${subActionIndex}.meta.gpFunding`)}
                        onChange={this.props.handleInputChange}
                        onBlur={this.props.handleControlBlur}
                      />
                    </FormInputWithError>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginTop: 25 }}>
              <Col span={10}>
                <p>
                  {isGrabPointCurrency && (
                    <Fragment>
                      <Link
                        style={{
                          fontSize: '1.5rem',
                          textDecoration: 'underline',
                        }}
                        to="https://wiki.grab.com/pages/viewpage.action?spaceKey=REW&title=%5BWikiHow%5D+VPCC+tagging+for+Points+Awarding"
                        target="_blank"
                      >
                        VPCC Guidelines
                      </Link>
                      <Row stype={{ marginTop: 10 }}>
                        <Col span={12} style={colStyle}>
                          <FormInputWithError
                            name={`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.vertical`}
                            {...this.props}
                          >
                            <Form.Input
                              label="Point VPCC Vertical"
                              required
                              value={_.get(
                                model,
                                `nodes[${currentNode}].data[${subActionIndex}].meta.financeData.vertical`,
                              )}
                              onChange={this.props.handleInputChange}
                              onBlur={this.props.handleControlBlur}
                            />
                          </FormInputWithError>
                        </Col>
                        <Col span={12} style={colStyle}>
                          <FormInputWithError
                            name={`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.product`}
                            {...this.props}
                          >
                            <Form.Input
                              label="Point VPCC Product"
                              required
                              value={_.get(
                                model,
                                `nodes[${currentNode}].data[${subActionIndex}].meta.financeData.product`,
                              )}
                              onChange={this.props.handleInputChange}
                              onBlur={this.props.handleControlBlur}
                            />
                          </FormInputWithError>
                        </Col>
                        <Col span={12} style={colStyle}>
                          {' '}
                          <FormInputWithError
                            name={`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.costCenter`}
                            {...this.props}
                          >
                            <Form.Input
                              label="Point VPCC Cost Center"
                              required
                              value={_.get(
                                model,
                                `nodes[${currentNode}].data[${subActionIndex}].meta.financeData.costCenter`,
                              )}
                              onChange={this.props.handleInputChange}
                              onBlur={this.props.handleControlBlur}
                            />
                          </FormInputWithError>
                        </Col>
                        <Col span={12} style={colStyle}>
                          {' '}
                          <FormInputWithError
                            name={`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.pricePerPoint`}
                            {...this.props}
                          >
                            <Form.Input
                              type="number"
                              required
                              label="Point VPCC Price Per Point"
                              value={_.get(
                                model,
                                `nodes[${currentNode}].data[${subActionIndex}].meta.financeData.pricePerPoint`,
                              )}
                              onChange={this.props.handleInputChange}
                              defaultValue={0}
                              onBlur={this.props.handleControlBlur}
                            />
                          </FormInputWithError>
                        </Col>
                        <Col span={24}>
                          <FormInputWithError
                            name={`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.fiatCurrency`}
                            {...this.props}
                          >
                            <Form.Input
                              label="Fiat Currency"
                              required
                              value={_.get(
                                model,
                                `nodes[${currentNode}].data[${subActionIndex}].meta.financeData.fiatCurrency`,
                              )}
                              onChange={this.props.handleInputChange}
                              onBlur={this.props.handleControlBlur}
                            />
                          </FormInputWithError>
                        </Col>
                      </Row>
                    </Fragment>
                  )}
                </p>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    );
  }
}

export function mapStateToProps(state: StoreState) {
  return {
    dynamicInputTypes: _.get(state, 'newTridents.dynamicInputTypes', {}),
    apcPurpose: _.get(state, 'newTridents.awardPointAction.purpose', []),
    apcBudgetSource: _.get(state, 'newTridents.awardPointAction.budgetSource', []),
  };
}

export default connect(mapStateToProps, {
  getDynamicInputTypes,
  getAwardPointAction,
})(ActionAwardPointsForm);
